/**
 * Lightweight markdown → HTML renderer.
 * Handles: headings, bold, italic, code blocks, inline code,
 * lists, tables (converted to interactive markup), and hr.
 *
 * The output is passed to `dangerouslySetInnerHTML`, so every caller
 * MUST run the result through `sanitizeHtml()` before injecting it
 * into the DOM — the LLM can produce arbitrary strings, including
 * raw HTML that would otherwise execute as script.
 */

// Tags we emit or knowingly accept from the renderer + the mapping/table
// helpers below. Anything else is stripped.
const ALLOWED_TAGS = new Set([
  "p", "br", "hr",
  "strong", "em", "b", "i", "u", "s",
  "code", "pre",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "table", "thead", "tbody", "tfoot", "tr", "th", "td",
  "div", "span", "input",
]);

// Only these attributes — on any tag — are allowed through. No `on*`
// handlers, no `style` (would allow `expression()` on old browsers +
// arbitrary CSS payloads), no `href`/`src` (markdown doesn't emit them
// in our renderer, so their presence would be an injection).
const ALLOWED_ATTRS = new Set([
  "class",
  "data-table-id",
  "data-table",
  "data-col",
  "data-count",
  "data-orig-idx",
  "placeholder",
]);

/**
 * Whitelist-based HTML sanitizer. Walks the DOM, strips unknown tags
 * (lifting their children), and removes disallowed attributes. Intended
 * to be paired with our controlled markdown renderer — not a general-
 * purpose sanitizer, and should not be used on untrusted free-form HTML.
 */
export function sanitizeHtml(html: string): string {
  if (typeof document === "undefined") return html; // SSR safety
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  sanitizeNode(tpl.content);
  return tpl.innerHTML;
}

function sanitizeNode(root: Node): void {
  // Collect disallowed elements first so we don't mutate while iterating.
  const toReplace: Element[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === 1) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        toReplace.push(el);
        return; // children handled after lifting
      }
      // Strip disallowed attributes.
      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (!ALLOWED_ATTRS.has(name)) {
          el.removeAttribute(attr.name);
        }
      }
    }
    for (const child of Array.from(node.childNodes)) walk(child);
  };
  walk(root);

  // Lift children of disallowed elements into their parent, then remove.
  for (const el of toReplace) {
    const parent = el.parentNode;
    if (!parent) continue;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }
}

export function renderMarkdown(text: string): string {
  // Extract mapping blocks before generic code block handling
  let html = text
    .replace(/```mapping\n([\s\S]*?)```/g, (_m, body: string) => buildMappingBlock(body))
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^---$/gm, '<hr>');

  // Table parsing
  const lines = html.split('\n');
  let inTable = false;
  let isHeader = true;
  let headerRow: string[] | null = null;
  let tableRows: string[][] = [];
  const result: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (!inTable) {
        inTable = true;
        isHeader = true;
        headerRow = null;
        tableRows = [];
      }
      if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
        isHeader = false;
        continue;
      }
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim());
      if (isHeader) {
        headerRow = cells;
        isHeader = false;
      } else {
        tableRows.push(cells);
      }
    } else {
      if (inTable && headerRow) {
        result.push(buildTable(headerRow, tableRows));
        headerRow = null;
        tableRows = [];
        inTable = false;
      }
      if (/^[-*] (.+)$/.test(trimmed)) {
        result.push('<li>' + trimmed.replace(/^[-*] /, '') + '</li>');
      } else if (/^\d+\. (.+)$/.test(trimmed)) {
        result.push('<li>' + trimmed.replace(/^\d+\. /, '') + '</li>');
      } else {
        result.push(trimmed);
      }
    }
  }
  if (inTable && headerRow) result.push(buildTable(headerRow, tableRows));

  html = result.join('\n');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
  html = html.replace(/\n\n+/g, '</p><p>');
  html = html.replace(/\n/g, ' ');
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/(<\/(?:table|pre|ul|ol|h[1-3]|hr|div)>)\s/g, '$1');

  return '<p>' + html + '</p>';
}

let _tblId = 0;

function buildTable(headers: string[], rows: string[][]): string {
  const tid = `tbl_${++_tblId}`;
  const dataRows: string[][] = [];
  const footRows: string[][] = [];
  for (const row of rows) {
    if (row.some(c => /<strong>/.test(c) || /\*\*/.test(c))) footRows.push(row);
    else dataRows.push(row);
  }

  let h = `<div class="table-wrapper" data-table-id="${tid}">`;
  // Only show filter inputs when there are multiple data rows
  if (dataRows.length > 1) {
    h += '<div class="table-filter-row">';
    for (let i = 0; i < headers.length; i++) {
      h += `<input class="table-filter-input" data-col="${i}" data-table="${tid}" placeholder="Filter ${esc(headers[i].replace(/<[^>]+>/g, ''))}" />`;
    }
    h += '</div>';
  }
  h += '<table><thead><tr>';
  for (let i = 0; i < headers.length; i++) {
    h += `<th class="sortable" data-col="${i}" data-table="${tid}">${headers[i]}</th>`;
  }
  h += '</tr></thead><tbody>';
  for (let ri = 0; ri < dataRows.length; ri++) {
    h += `<tr data-orig-idx="${ri}">${dataRows[ri].map(c => '<td>' + c + '</td>').join('')}</tr>`;
  }
  h += '</tbody>';
  if (footRows.length) {
    h += '<tfoot>';
    for (const row of footRows) h += '<tr>' + row.map(c => '<td>' + c + '</td>').join('') + '</tr>';
    h += '</tfoot>';
  }
  h += `</table>`;
  if (dataRows.length > 1) {
    h += `<div class="table-row-count" data-count="${tid}">${dataRows.length} rows</div>`;
  }
  h += `</div>`;
  return h;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildMappingBlock(body: string): string {
  const lines = body.trim().split('\n');
  let mappingRows: { left: string; right: string; badge: string; mapped: boolean }[] = [];
  let staticRows: { field: string; value: string }[] = [];
  let inStatic = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^---\s*static\s*---$/i.test(line)) { inStatic = true; continue; }

    if (inStatic) {
      const eqIdx = line.indexOf('=');
      if (eqIdx > 0) {
        staticRows.push({ field: line.slice(0, eqIdx).trim(), value: line.slice(eqIdx + 1).trim() });
      }
    } else {
      const arrowIdx = line.indexOf('→');
      if (arrowIdx < 0) continue;
      const left = line.slice(0, arrowIdx).trim();
      let rightRaw = line.slice(arrowIdx + 1).trim();
      // Extract badge tags like (transform), (lookup) from the end
      let badge = '';
      const badgeMatch = rightRaw.match(/\((transform|lookup)\)\s*$/);
      if (badgeMatch) {
        badge = badgeMatch[1];
        rightRaw = rightRaw.slice(0, rightRaw.length - badgeMatch[0].length).trim();
      }
      const mapped = rightRaw.length > 0;
      mappingRows.push({ left, right: mapped ? rightRaw : '—', badge, mapped });
    }
  }

  let h = '<div class="mapping-block">';
  h += '<div class="mapping-header"><span class="mapping-col-label">Excel Column</span><span class="mapping-col-label">CRM Field</span></div>';
  for (const row of mappingRows) {
    const cls = row.mapped ? 'mapping-row' : 'mapping-row mapping-unmapped';
    h += `<div class="${cls}">`;
    h += `<span class="mapping-left">${esc(row.left)}</span>`;
    h += `<span class="mapping-arrow">${row.mapped ? '→' : '╌╌'}</span>`;
    h += `<span class="mapping-right">${esc(row.right)}`;
    if (row.badge) h += ` <span class="mapping-badge">${esc(row.badge)}</span>`;
    h += `</span></div>`;
  }
  if (staticRows.length) {
    h += '<div class="mapping-static-header">Fixed Values</div>';
    for (const s of staticRows) {
      h += `<div class="mapping-row mapping-static"><span class="mapping-left">—</span><span class="mapping-arrow">=</span><span class="mapping-right">${esc(s.field)} = <strong>${esc(s.value)}</strong></span></div>`;
    }
  }
  h += '</div>';
  return h;
}

/**
 * Attach event listeners for interactive tables in a container.
 * Call this after injecting markdown HTML into the DOM.
 */
export function attachTableInteractivity(container: HTMLElement): () => void {
  const cleanups: (() => void)[] = [];

  // Sort handlers
  const sortHeaders = container.querySelectorAll<HTMLElement>('th.sortable');
  for (const th of Array.from(sortHeaders)) {
    const handler = () => handleSort(th);
    th.addEventListener('click', handler);
    cleanups.push(() => th.removeEventListener('click', handler));
  }

  // Filter handlers
  const filterInputs = container.querySelectorAll<HTMLInputElement>('.table-filter-input');
  for (const inp of Array.from(filterInputs)) {
    const handler = () => handleFilter(inp);
    inp.addEventListener('input', handler);
    cleanups.push(() => inp.removeEventListener('input', handler));
  }

  return () => cleanups.forEach(fn => fn());
}

function handleSort(th: HTMLElement) {
  const tid = th.dataset.table;
  const colIdx = parseInt(th.dataset.col || '0', 10);
  const wrapper = document.querySelector(`[data-table-id="${tid}"]`);
  if (!wrapper) return;

  const table = wrapper.querySelector('table')!;
  const tbody = table.querySelector('tbody')!;
  const allTh = table.querySelectorAll('thead th');

  let dir = 'asc';
  if (th.classList.contains('sort-asc')) dir = 'desc';
  else if (th.classList.contains('sort-desc')) dir = 'none';

  allTh.forEach(t => t.classList.remove('sort-asc', 'sort-desc'));
  if (dir === 'none') {
    const rows = Array.from(tbody.querySelectorAll('tr'));
    rows.sort((a, b) => (parseInt(a.getAttribute('data-orig-idx') || '0') - parseInt(b.getAttribute('data-orig-idx') || '0')));
    rows.forEach(r => tbody.appendChild(r));
    return;
  }

  th.classList.add('sort-' + dir);
  const rows = Array.from(tbody.querySelectorAll('tr'));
  rows.sort((a, b) => {
    const aC = a.cells[colIdx]?.textContent || '';
    const bC = b.cells[colIdx]?.textContent || '';
    const aN = parseNum(aC);
    const bN = parseNum(bC);
    const cmp = aN !== null && bN !== null ? aN - bN : aC.localeCompare(bC, undefined, { sensitivity: 'base' });
    return dir === 'desc' ? -cmp : cmp;
  });
  rows.forEach(r => tbody.appendChild(r));
}

function handleFilter(inp: HTMLInputElement) {
  const tid = inp.dataset.table;
  const wrapper = document.querySelector(`[data-table-id="${tid}"]`);
  if (!wrapper) return;

  const filters: { col: number; val: string }[] = [];
  wrapper.querySelectorAll<HTMLInputElement>('.table-filter-input').forEach(i => {
    filters.push({ col: parseInt(i.dataset.col || '0', 10), val: i.value.toLowerCase().trim() });
  });

  const tbody = wrapper.querySelector('tbody')!;
  const rows = tbody.querySelectorAll('tr');
  let visible = 0;

  rows.forEach(row => {
    let show = true;
    for (const f of filters) {
      if (!f.val) continue;
      const cell = (row as HTMLTableRowElement).cells[f.col];
      if (!cell || !cell.textContent!.toLowerCase().includes(f.val)) {
        show = false;
        break;
      }
    }
    (row as HTMLElement).style.display = show ? '' : 'none';
    if (show) visible++;
  });

  const cnt = wrapper.querySelector(`[data-count="${tid}"]`);
  if (cnt) cnt.textContent = `${visible} of ${rows.length} rows`;
}

function parseNum(s: string): number | null {
  const clean = s.replace(/<[^>]+>/g, '').replace(/[£$€,]/g, '').trim();
  const n = parseFloat(clean);
  return isNaN(n) ? null : n;
}
